import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LineChart from "../components/charts/LineChart";
import DaugeChart from "../components/charts/DaugeChart";
import { useCurrent, useFetch, useInvoice } from "../hooks";
import InvListWidget from "../components/invoice/InvListWidget";
import { AiOutlineDashboard } from "react-icons/ai";
import { MwButton, MwSelector } from "../components/ui";
import { MdDashboard, MdSupportAgent } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import i18next from "i18next";
import axios from "axios";
import CardHeadeWithActions from "../components/ui/CardHeadeWithActions";

const toNumberValue = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (Array.isArray(value)) {
    const parsed = Number(value[0]);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === "object") {
    const candidates = [
      value.total,
      value.sum,
      value.value,
      value.amount,
      value.data,
      value.result,
    ];
    for (const item of candidates) {
      const parsed = Number(item);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
};

const DashBoard = () => {
  const [t] = useTranslation("global");
  const params = useParams();
  const {
    currentSettings,
    currentTenantId,
    currentCompanyId,
    currentBranchId,
  } = useCurrent();
  const { formatter } = useInvoice();
  const date = new Date();
  const currentMonth = date.getMonth() + 1;
  const currentYeay = date.getFullYear();
  const _token = localStorage.getItem("token");
  const isArabic = (i18next.language || "ar").toLowerCase().startsWith("ar");
  const [selectedYearItem, setSelectedYearItem] = useState({
    label: String(currentYeay),
    value: currentYeay,
  });
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const selectedYear = Number(selectedYearItem?.value || currentYeay);
  const [availableYears, setAvailableYears] = useState([currentYeay]);
  const yearsCheckStartedRef = useRef(false);

  const dashboardReady = Boolean(
    currentTenantId && currentCompanyId && currentBranchId,
  );
  const SETTING_TARGET_MONTHLY =
    currentSettings?.generalSettings?.TARGET_MONTHLY;
  const targetMonthlyNumber = Number(SETTING_TARGET_MONTHLY || 0);

  const baseYears = useMemo(
    () => [currentYeay, currentYeay - 1, currentYeay - 2],
    [currentYeay],
  );

  const yearOptions = useMemo(() => {
    return (availableYears || []).map((year) => ({
      label: String(year),
      value: year,
    }));
  }, [availableYears]);

  const monthOptions = useMemo(
    () => [
      { value: 1, label: t("DashBoard.months.january") },
      { value: 2, label: t("DashBoard.months.february") },
      { value: 3, label: t("DashBoard.months.march") },
      { value: 4, label: t("DashBoard.months.april") },
      { value: 5, label: t("DashBoard.months.may") },
      { value: 6, label: t("DashBoard.months.june") },
      { value: 7, label: t("DashBoard.months.july") },
      { value: 8, label: t("DashBoard.months.august") },
      { value: 9, label: t("DashBoard.months.september") },
      { value: 10, label: t("DashBoard.months.october") },
      { value: 11, label: t("DashBoard.months.november") },
      { value: 12, label: t("DashBoard.months.december") },
    ],
    [t],
  );

  const selectedMonthLabel =
    monthOptions.find((item) => item.value === selectedMonth)?.label || "";
  const monthlyTargetLabel = isArabic
    ? `الهدف لشهر ${selectedMonthLabel}`
    : `Target for ${selectedMonthLabel}`;
  const yearPurchasesLabel = isArabic
    ? `مشتريات سنة ${selectedYear}`
    : `${t("DashBoard.currentYearPurchases")} ${selectedYear}`;
  const yearPurchasesPaidLabel = isArabic
    ? `مشتريات سنة ${selectedYear} / مدفوع`
    : `${t("DashBoard.currentYearPurchases")} ${selectedYear} / ${t("DashBoard.paid")}`;
  const yearPurchasesUnpaidLabel = isArabic
    ? `مشتريات سنة ${selectedYear} / غير مدفوع`
    : `${t("DashBoard.currentYearPurchases")} ${selectedYear} / ${t("DashBoard.unpaid")}`;

  useEffect(() => {
    if (!dashboardReady) return;
    if (yearsCheckStartedRef.current) return;
    yearsCheckStartedRef.current = true;
    const cacheKey = `dashboard_years_${currentTenantId}_${currentCompanyId}_${currentBranchId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setAvailableYears(parsed);
          return;
        }
      } catch (e) {}
    }
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const baseUrl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
    const requests = baseYears.map((year) =>
      axios
        .get(
          `${baseUrl}/invoices/year/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${year}`,
          { headers },
        )
        .then((response) => ({ year, value: Number(response.data || 0) }))
        .catch(() => ({ year, value: 0 })),
    );
    Promise.all(requests).then((results) => {
      const available = results
        .filter((item) => Number(item.value || 0) > 0)
        .map((item) => item.year);
      const nextYears = available.length > 0 ? available : [];
      setAvailableYears(nextYears);
      localStorage.setItem(cacheKey, JSON.stringify(nextYears));
    });
  }, [
    baseYears,
    currentBranchId,
    currentCompanyId,
    currentTenantId,
    currentYeay,
    dashboardReady,
  ]);

  useEffect(() => {
    if (!availableYears || availableYears.length === 0) return;
    if (!availableYears.includes(selectedYearItem?.value)) {
      const nextYear = availableYears[0];
      setSelectedYearItem({ label: String(nextYear), value: nextYear });
    }
  }, [availableYears, selectedYearItem]);

  const targetMonthlyUrl = useMemo(() => {
    if (!dashboardReady) return null;
    return `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/invoices/month/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${selectedMonth}`;
  }, [
    currentBranchId,
    currentCompanyId,
    currentTenantId,
    dashboardReady,
    selectedMonth,
  ]);

  const yearlyTotalUrl = useMemo(() => {
    if (!dashboardReady) return null;
    return `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/invoices/year/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${selectedYear}`;
  }, [
    currentBranchId,
    currentCompanyId,
    currentTenantId,
    dashboardReady,
    selectedYear,
  ]);

  const paidUrl = useMemo(() => {
    if (!dashboardReady) return null;
    return `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/invoices/paid/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${selectedYear}/true`;
  }, [
    currentBranchId,
    currentCompanyId,
    currentTenantId,
    dashboardReady,
    selectedYear,
  ]);

  const salesMonthsUrl = useMemo(() => {
    if (!dashboardReady) return null;
    return `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/invoices/month-group/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${selectedYear}`;
  }, [
    currentBranchId,
    currentCompanyId,
    currentTenantId,
    dashboardReady,
    selectedYear,
  ]);

  const { data: targetMonthlyData, loading: targetMonthyLoading } = useFetch(
    targetMonthlyUrl,
    true,
    { cacheTimeMs: 60_000 },
  );
  const { data: yearlyTotalData } = useFetch(yearlyTotalUrl, true, {
    cacheTimeMs: 60_000,
  });
  const { data: paidData, loading: paidInvoiceLoading } = useFetch(
    paidUrl,
    true,
    { cacheTimeMs: 60_000 },
  );
  const { data: salesMonthsData, loading: salesMonthsLoading } = useFetch(
    salesMonthsUrl,
    true,
    { cacheTimeMs: 60_000 },
  );

  const targetMonthlySum = toNumberValue(targetMonthlyData);
  const purchasesCurrentYear = toNumberValue(yearlyTotalData);
  const paidSum = toNumberValue(paidData);

  const targetMonthyPercent = useMemo(() => {
    const percentSum =
      targetMonthlyNumber > 0
        ? (targetMonthlySum / targetMonthlyNumber) * 100
        : 0;
    return [percentSum, Math.max(0, 100 - percentSum)];
  }, [targetMonthlyNumber, targetMonthlySum]);

  const targetMonthyLabel = useMemo(() => {
    return [targetMonthlySum, targetMonthlyNumber];
  }, [targetMonthlyNumber, targetMonthlySum]);

  const paidInvoicePercent = useMemo(() => {
    const base = Number(purchasesCurrentYear || 0);
    const percentSum = base > 0 ? (paidSum / base) * 100 : 0;
    return [percentSum, Math.max(0, 100 - percentSum)];
  }, [paidSum, purchasesCurrentYear]);

  const paidInvoiceLabel = useMemo(() => {
    const base = Number(purchasesCurrentYear || 0);
    return [paidSum, Math.max(0, base - paidSum)];
  }, [paidSum, purchasesCurrentYear]);

  const salesMonths = Array.isArray(salesMonthsData) ? salesMonthsData : [];

  const purchasesSummaryCards = useMemo(() => {
    const paid = Number(paidInvoiceLabel?.[0] ?? 0);
    const unpaid = Number(paidInvoiceLabel?.[1] ?? 0);
    return [
      { label: yearPurchasesLabel, value: Number(purchasesCurrentYear || 0) },
      { label: yearPurchasesPaidLabel, value: paid },
      { label: yearPurchasesUnpaidLabel, value: unpaid },
    ];
  }, [
    paidInvoiceLabel,
    purchasesCurrentYear,
    yearPurchasesLabel,
    yearPurchasesPaidLabel,
    yearPurchasesUnpaidLabel,
  ]);

  return (
    <>
      <div className="flex flex-col gap-6 pb-3">
        <CardHeadeWithActions
          title={t("menu.Dashboard")}
          icon={<MdDashboard size={20} />}
        >
          <div className="flex items-center gap-2">
            {yearOptions.length > 0 && (
              <div className="flex items-center">
                <MwSelector
                  label=""
                  initalValue={String(currentYeay)}
                  _data={yearOptions}
                  dataType="labelValue"
                  withSearch={false}
                  withLabel={false}
                  withAddNew={false}
                  selectedItem={selectedYearItem}
                  setSelectedItem={setSelectedYearItem}
                />
              </div>
            )}
            <Link
              target="_blank"
              to={`${process.env.REACT_APP_WEB_SUPPORT_BASE_URL}/${params.tenant}/token/${_token}`}
            >
              <MwButton classNameCustom="w-full hover:shadow-lg transition-all duration-200" inGroup={true} type="saveBtn">
                <MdSupportAgent size={20} /> {t("DashBoard.support")}{" "}
              </MwButton>
            </Link>
          </div>
        </CardHeadeWithActions>
        <div className="flex flex-col px-3 mt-[-10px] gap-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 order-2 xl:order-1 h-full flex flex-col gap-6">
              <div className="bg-white rounded-3xl shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-100">
                <LineChart
                  datasetData={salesMonths}
                  datasetLabel={yearPurchasesLabel}
                  loading={salesMonthsLoading}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {purchasesSummaryCards.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-300 hover:scale-105 transform"
                  >
                    <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">{item.label}</div>
                    <div className="text-xl font-bold text-gray-900 leading-tight">
                      {formatter.format(Number(item.value || 0))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-4 flex-1 min-h-0">
                <InvListWidget />
              </div>
            </div>
            <div className="xl:col-span-1 grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-1 gap-6 order-1 xl:order-2">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 border border-gray-200 rounded-3xl bg-white p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                <DaugeChart
                  labels={[t("DashBoard.actual"), t("DashBoard.target")]}
                  datasetLabel={monthlyTargetLabel}
                  labelMetrics={SETTING_TARGET_MONTHLY}
                  datasetData={targetMonthyPercent}
                  datasetDataLabel={targetMonthyLabel}
                  loading={targetMonthyLoading}
                  className="sm:col-span-9 border-0 bg-transparent px-1"
                />
                <div className="sm:col-span-3 grid grid-cols-3 sm:grid-cols-1 gap-2 content-start">
                  {monthOptions.map((month) => {
                    const isFutureMonth =
                      selectedYear > currentYeay ||
                      (selectedYear === currentYeay &&
                        month.value > currentMonth);
                    return (
                      <button
                        type="button"
                        key={month.value}
                        disabled={isFutureMonth}
                        onClick={() => {
                          if (isFutureMonth) return;
                          setSelectedMonth(month.value);
                        }}
                        className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all duration-200 transform hover:scale-105 ${selectedMonth === month.value ? "brand-bg text-white brand-border shadow-md" : "bg-white brand-text border-gray-200 hover:border-gray-300 hover:shadow-sm"} ${isFutureMonth ? "opacity-40 cursor-not-allowed hover:scale-100" : ""}`}
                      >
                        {month.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="bg-white rounded-3xl shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-100">
                <DaugeChart
                  labels={[t("DashBoard.paid"), t("DashBoard.unpaid")]}
                  datasetLabel={yearPurchasesLabel}
                  labelMetrics={purchasesCurrentYear}
                  datasetData={paidInvoicePercent}
                  datasetDataLabel={paidInvoiceLabel}
                  loading={paidInvoiceLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashBoard;
